// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Cart } from "@insite/client-framework/Services/CartService";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canCheckoutWithCart,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByNodeId, LinkModel } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { USER_ROLES } from "@synd/XDL/constants";

export const xIsAuthenticated = (state: ApplicationState): boolean => {
    const session = getSession(state);
    return session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
};

export const xHasRole = (state: ApplicationState, role: USER_ROLES, exactMatch = false) => {
    const roles = state.context.session.userRoles;
    if (exactMatch) {
        return roles === role; // User don't have other roles
    }
    return roles?.indexOf(role) > -1;
};

export const xIsCheckoutDisabled = (cart?: Cart): boolean =>
    !canCheckoutWithCart(cart) || !!hasOnlyQuoteRequiredProducts(cart) || !!isCartCheckoutDisabled(cart);

export const getLogoImage = (state: ApplicationState) => {
    const logoImage = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/Logo";
    })?.fields.logoImage;
    return logoImage;
};

export const getMainNavigationWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/MainNavigation";
    })?.fields;
};
export const getHeaderSearchWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/HeaderSearchInput";
    })?.fields;
};

export const getCartLinkWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Common/CartLink";
    })?.fields;
};

export const getLogoImageWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/Logo";
    })?.fields;
};

export const getHeaderActionsWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "HeaderActions/HeaderActions";
    })?.fields;
};

export const getSecondaryNavigationWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/SecondaryNavigation";
    })?.fields;
};

export const getHeaderLinkListWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/HeaderLinkList";
    })?.fields;
};

export const getCommonLinksWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).filter((widget: WidgetProps) => {
        return widget.type === "Basic/SocialLinks";
    });
};

export const getHeaderSignInWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Common/HeaderSignIn";
    })?.fields;
};

export const getMyAccountMapLinks = (state: ApplicationState, widgetLinks: LinkModel[]): PageLinkModel[] => {
    return widgetLinks.reduce<PageLinkModel[]>((acc, widgetLink) => {
        const destinationId = widgetLink.fields.destination.value;
        const overriddenTitle = widgetLink.fields.overriddenTitle;

        const pageLink = getPageLinkByNodeId(state, destinationId);
        if (!pageLink || pageLink.excludeFromNavigation) {
            return acc;
        }

        const { type, title } = pageLink;
        const link: PageLinkModel = { ...pageLink, title: overriddenTitle || title };

        acc.push(link);

        return acc;
    }, []);
};
